import {
  type ChatMessage,
  type QueravelConversation,
  queravelConversationMessages,
  queravelConversationName,
  queravelConversations,
  shortenQuestion,
} from "@rhema/client";
import { ChatRow, EmptyState, MessageIcon, Tag } from "@rhema/ui";
import { useMemo } from "react";
import { listTime } from "../data/view";
import { useMenus } from "../menus/actions";
import { WithMenu } from "../menus/WithMenu";
import { useClientState } from "../session/session";
import { useQueravelWritingIn } from "./useQueravel";

/** Where a link opens one conversation in the Queravel panel. */
export function conversationHref(id: string): string {
  return `/queravel?c=${encodeURIComponent(id)}`;
}

/** The Queravel chat's stored messages and the conversations in them, newest first. */
export function useQueravelConversations(assistantId: string | null | undefined) {
  const state = useClientState();
  const messages = useMemo<ChatMessage[]>(
    () => (assistantId ? (state.messages[assistantId] ?? []) : []),
    [state.messages, assistantId],
  );
  const conversations = useMemo(() => queravelConversations(messages), [messages]);
  return { messages, conversations };
}

function ConversationIcon() {
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-surface-header text-ink-secondary"
    >
      <MessageIcon size={16} />
    </span>
  );
}

const writingLine = <span className="truncate italic text-blue">Queravel is writing…</span>;

/**
 * Every Queravel conversation, newest first, each labelled with the chat it was about. It is the
 * panel's History. Picking one opens it in the panel.
 */
export function QueravelHistory({
  assistantId,
  selectedId,
  onOpen,
}: {
  assistantId: string | null;
  selectedId: string | null;
  onOpen: (id: string) => void;
}) {
  const { messages, conversations } = useQueravelConversations(assistantId);
  const writing = useQueravelWritingIn();
  const menus = useMenus();
  // A conversation whose first answer is still being written has nothing stored yet.
  const unsaved = writing.filter((w) => !conversations.some((c) => c.id === w.id));
  const writingIds = new Set(writing.map((w) => w.id));

  const menuOf = (c: QueravelConversation) => () =>
    assistantId
      ? menus.queravelConversation(assistantId, {
          id: c.id,
          ids: queravelConversationMessages(messages, c.id),
          name: queravelConversationName(c),
          title: c.title,
        })
      : [];

  if (!conversations.length && !unsaved.length) {
    return (
      <EmptyState icon={<MessageIcon size={26} />} title="No conversations yet" className="flex-1">
        Your questions to Queravel and its answers are kept here on your devices.
      </EmptyState>
    );
  }

  return (
    <ul
      aria-label="Queravel conversations"
      className="m-0 flex min-h-0 flex-1 list-none flex-col gap-0.5 overflow-y-auto px-2.5 py-2"
    >
      {unsaved.map((w) => (
        <li key={w.id}>
          <ChatRow
            onClick={() => onOpen(w.id)}
            density="compact"
            selected={w.id === selectedId}
            title={w.question ? shortenQuestion(w.question) : "New conversation"}
            avatar={<ConversationIcon />}
            time=""
            preview={writingLine}
          />
        </li>
      ))}
      {conversations.map((c) => {
        const name = queravelConversationName(c);
        return (
          <WithMenu key={c.id} label={name} items={menuOf(c)}>
            <ChatRow
              onClick={() => onOpen(c.id)}
              density="compact"
              selected={c.id === selectedId}
              title={name}
              tag={
                c.source ? (
                  <Tag className="block max-w-[45%] truncate bg-queravel-tint text-queravel">
                    {c.source.name}
                  </Tag>
                ) : undefined
              }
              avatar={<ConversationIcon />}
              time={listTime(c.lastAt)}
              preview={writingIds.has(c.id) ? writingLine : shortenQuestion(c.lastText, 90)}
            />
          </WithMenu>
        );
      })}
    </ul>
  );
}
